import { action, computed, observable } from 'mobx'
import { distinctUntilChanged, map, merge, switchMap } from 'rxjs'
import { isEqual, uniqBy } from 'lodash-es'
import { type CallCenterJobSummary } from './call-center-job-summary'
import type { PublishedLegendFn, SearchJobsFn } from '@/features/jobs'
import {
  LegendVersionId,
  type PublishedLegend,
} from '@/features/jobs/legends/PublishedLegend'
import { tapAction, toStream } from '@/utils/mobx-util'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { CallCenterJobHistoryItemViewModel } from './CallCenterJobHistoryItemViewModel'
import { mapSearchableJobToCallCenterJobSummary } from './call-center-job-summary-mappers'
import type { AlertRetryWrapperFactory } from '@/features/shell'

const SEARCH_LIMIT = 25

export class CallCenterJobHistoryViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @observable.shallow private accessor _legends = new Map<
    string,
    PublishedLegend | undefined
  >()

  private readonly _jobVms = new ViewModelList({
    models: () => this.jobsScroller.items.slice(),
    create: (job: CallCenterJobSummary) =>
      new CallCenterJobHistoryItemViewModel(job, (j) => this._isNavigable(j)),
  })

  @observable.ref
  accessor clientUserPublicIds: Id[] = []

  readonly jobsScroller = new InfiniteScrollProvider<CallCenterJobSummary>({
    limit: SEARCH_LIMIT,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading the job history failed',
        signal,
      )
      return withRetry(() =>
        this._fetchJobSummaries(paging, this.clientUserPublicIds, signal),
      )
    },
  })

  constructor(
    private readonly _searchJobs: SearchJobsFn,
    private readonly _publishedLegend: PublishedLegendFn,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this._lifecycle.manage(
      toStream(() => this._draftLegendVersionIds, true).pipe(
        distinctUntilChanged<LegendVersionId[]>(isEqual),
        switchMap((legendVersionIds) =>
          merge(
            ...legendVersionIds.map((legendVersionId) =>
              this._publishedLegend(legendVersionId).pipe(
                map(
                  (legend) =>
                    [
                      LegendVersionId.asString(legendVersionId),
                      legend,
                    ] as const,
                ),
              ),
            ),
          ),
        ),
        tapAction(([key, legend]) => {
          this._legends.set(key, legend)
        }),
      ),
    )
    this._lifecycle.manage(this._jobVms)
  }

  @computed
  get jobs() {
    return this.jobsScroller.items
  }

  /**
   * The job history rows.
   */
  @computed
  get jobVms() {
    return this._jobVms.items
  }

  @computed
  private get _draftLegendVersionIds() {
    return uniqBy(
      this.jobs
        .filter((job) => job.status === 'UnderConstruction')
        .flatMap((job) =>
          Id.some(job.legendId)
            ? [LegendVersionId(job.legendId, job.legendVersion)]
            : [],
        ),
      LegendVersionId.asString,
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the job history of the given users.
   */
  @action.bound
  fetchJobs(userPublicIds: Id[]): void {
    this.jobsScroller.reset()
    this.clientUserPublicIds = userPublicIds

    // A failed fetch shows in the job history card through the scroller's state.
    this.jobsScroller
      .fetch({ signal: this._lifecycle.signal })
      .catch(() => null)
  }

  @action.bound
  clear() {
    this.jobsScroller.reset()
    this.clientUserPublicIds = []
  }

  /**
   * Only tax drafts can be continued in the call center.
   */
  private _isNavigable(job: CallCenterJobSummary) {
    if (job.status !== 'UnderConstruction' || !Id.some(job.legendId)) {
      return false
    }

    const legend = this._legends.get(
      LegendVersionId.asString(
        LegendVersionId(job.legendId, job.legendVersion),
      ),
    )

    return legend?.serviceType === 'Tax'
  }

  private async _fetchJobSummaries(
    paging: CursorPageQuery,
    clientPublicIds: Id[],
    signal: AbortSignal,
  ): Promise<CursorPaged<CallCenterJobSummary>> {
    if (clientPublicIds.length === 0) {
      return CursorPaged.empty()
    }

    try {
      const response = await this._searchJobs(
        {
          memberIds: clientPublicIds.filter(Id.some),
          limit: paging.limit,
          scope: 'All',
          orderBy: 'date_created desc',
          after: paging.after ?? undefined,
        },
        signal,
      )

      signal.throwIfAborted()

      const clientIdSet = new Set(clientPublicIds)
      const jobs = response.data
        .filter((job) => {
          const championClient = job.championClient
          if (championClient) {
            return clientIdSet.has(championClient.id)
          }

          return job.members.some(
            (member) => clientIdSet.has(member.id) && member.type === 'Client',
          )
        })
        .map(mapSearchableJobToCallCenterJobSummary)

      return {
        data: jobs,
        next: response.next,
        total: response.total,
      }
    } catch (error) {
      if (signal.aborted) {
        return CursorPaged.empty()
      }
      throw error
    }
  }
}
